import { useQuery } from '@tanstack/react-query';
import warehouseService from '@/services/warehouse/warehouse-service';
import helper from '@/utils/helper';
import { ActionIcon, Box, Button, Card, Container, Flex, Text, Title, Tooltip } from '@mantine/core';
import { IconArrowLeft, IconEdit, IconTruck, IconUser } from '@tabler/icons-react';
import Link from 'next/link';
import { colors } from '@/theme';
import WarehouseStockItemList from '@/PageComponents/Warehouse/WarehouseStockItemList';
import Breadcrumbs from '@/PageComponents/Layout/Breadcrumbs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StockTransaction, StockTransactionLine, Warehouse } from '@/interfaces/api/models';
import { useRouter } from 'next/router';
import permissionService from '@/services/permission/permission-service';
import * as Enums from '@/utils/enums';
import ManageStockTransactionDrawer from '@/PageComponents/StockTransaction/ManageStockTransactionDrawer';
import stockTransactionService from '@/services/stock-transaction/stock-transaction-service';
import storage from '@/utils/storage';
import time from '@/utils/time';
import employeeService from '@/services/employee/employee-service';
import { createFileRoute } from '@tanstack/react-router'

function VanPage() {

    const router = useRouter();
    const { id } = router.query as { id: string }; // TODO(tanstack-router): verify router.query → useParams/useSearch
    const [stockAdmin] = useState(permissionService.hasPermission(Enums.PermissionName.StockTakeManager));
    const [stockTransfer] = useState(permissionService.hasPermission(Enums.PermissionName.StockTransactionTransfer));
    const [vanManager] = useState(permissionService.hasPermission(Enums.PermissionName.VanManage));
    const [createStockTransactionType, setCreateStockTransactionType] = useState<number | null>(null);
    const [selectedStockTransaction, setSelectedStockTransaction] = useState<any>();
    const initialStockTransaction = useRef<any>();
    const [refreshData, setRefreshData] = useState(true);

    const [van, setVan] = useState<Warehouse>();

    // TBD - if this is my van, should I always be able to do stock take? This has implications for the stock take feature
    // If this is my van, should I always be able to do stock transfer? How would this work?
    const isMyVan = useMemo(() => {
        return van?.EmployeeID === storage.getCookie(Enums.Cookie.employeeID);
    }, [van]);

    const { data, isLoading, error } = useQuery(['van', id], () => warehouseService.getWarehouse(id), {
        enabled: !!id
    });

    // Update van state when query data changes
    useEffect(() => {
        if (data) {
            setVan(data);
        }
    }, [data])

    const beginStockTransfer = async (direction: "in" | "out") => {

        let storeWarehouses = await warehouseService.getWarehouses(1000, undefined, Enums.WarehouseType.Warehouse);
        let storeWarehouse = storeWarehouses.Results?.find((w: any) => w.StoreID === van?.StoreID);
        let employeeID = storage.getCookie(Enums.Cookie.employeeID);
        let employee = await employeeService.getEmployee(employeeID);
        let lines: StockTransactionLine[] = [];

        if (direction === "out") {
            let vanStockItems = await warehouseService.searchWarehouseStocks({
                warehouseIDs: van?.ID ? [van.ID] : [],
                includeDisabled: false,
                pageSize: 1000,
                pageIndex: 0,
                searchPhrase: '',
                sortDirection: 'ascending',
                sortExpression: 'InventoryCode'
            });
            lines = vanStockItems.Results.filter((x: any) => x.QuantityOnHand !== 0).map((item: any, idx: any) => ({
                InventoryID: item.InventoryID,
                LineNumber: idx + 1,
                Quantity: item.QuantityOnHand,
                UnitCostPrice: item.Inventory?.CostPrice || 0,
                UnitPrice: item.Inventory?.Price || 0,
                ID: helper.newGuid(),
                IsActive: true,
                Inventory: item.Inventory,
                WarehouseStock: item
            }));
        }

        initialStockTransaction.current = {
            ID: helper.newGuid(),
            IsActive: true,
            Date: time.toISOString(time.now()),
            StockTransactionStatus: Enums.StockTransactionStatus.Draft,
            StockTransactionType: Enums.StockTransactionType.Transfer,
            StockTransactionLines: lines,
            TotalPriceExcluding: 0,
            TotalPriceIncluding: 0,
            TotalTax: 0,
            SourceWarehouseID: direction === "out" ? van?.ID : storeWarehouse?.ID,
            DestinationWarehouseID: direction === "in" ? van?.ID : storeWarehouse?.ID,
            SourceWarehouse: direction === "out" ? van : storeWarehouse,
            DestinationWarehouse: direction === "in" ? van : storeWarehouse,
            EmployeeID: employee?.ID,
            Employee: employee,
        } as StockTransaction;

        setCreateStockTransactionType(initialStockTransaction.current.StockTransactionType);
    }

    useEffect(() => {
        if (!refreshData) {
            setRefreshData(true);
        }
    }, [refreshData]);

    if (isLoading || !van) return <Container size={"xl"} p="md">
        <Card shadow="sm" padding="lg" radius="md" withBorder flex={2} pos={'relative'}>
            <Box mb="md">
                <Title order={3} >Loading Van Details...</Title>
            </Box>
        </Card>
    </Container>;

    return (<>
        {/* <Box
            mt={'var(--mantine-spacing-sm)'}
            px={'var(--mantine-spacing-md)'}
            mb={0}
            pb={0}
            style={(theme) => ({ borderBottom: `${theme.colors.gray[2]} 2px solid` })}
        >
            <Flex my={'md'} align={'center'} gap={'sm'}>
                {id && <>
                    <Breadcrumbs
                        currPage={{
                            text: van?.Code || 'Van',
                            link: `/van/${router.query.id}`, // TODO(tanstack-router): verify router.query → useParams/useSearch
                            type: 'view'
                        }}
                    />
                </>

                }
            </Flex>
        </Box> */}
        <Container size={"xl"} p="md">
            <Card shadow="sm" padding="lg" radius="md" withBorder flex={2} pos={'relative'}>

                <Flex justify={"space-between"} >
                    <Box>
                        <Flex >
                            <Link
                                href={'/inventory/list?tab=vanstock'}
                            >
                                <Button variant='subtle' size='xs' mr="xs" leftSection={<IconArrowLeft size={16} />} >
                                    Back
                                </Button>
                            </Link>
                            <Title order={3} >Van Details</Title>
                        </Flex>
                        <Box mt={"0.5rem"}>
                            <Flex align={"end"} gap={"sm"} justify={"left"} >
                                <IconTruck size={24} color={colors.bluePrimary} />
                                <Text mt={"0.5rem"}>{van.Code} {!van.IsActive && <> [Deactivated]</>}</Text>
                            </Flex>
                            <Flex align={"end"} gap={"sm"} justify={"left"} >
                                <IconUser size={24} color={colors.bluePrimary} />
                                <Text mt={"0.5rem"}>{van.Employee?.FullName ?? "Not assigned to a user"}</Text>
                            </Flex>
                        </Box>
                    </Box>

                    <Box>

                        {vanManager &&
                            <Tooltip label={'Edit Details'} color={'scBlue'} openDelay={500}>
                                <Link
                                    href={`/van/${van.ID}/edit`}
                                    style={{ position: 'absolute', top: '10px', right: '10px' }}
                                >
                                    <ActionIcon variant={'light'}>
                                        <IconEdit />
                                    </ActionIcon>
                                </Link>
                            </Tooltip>
                        }
                    </Box>
                </Flex>
            </Card>

            <Card shadow="sm" padding="lg" radius="md" withBorder flex={2} pos={'relative'} mt={"md"}>

                {refreshData &&
                    <WarehouseStockItemList
                        warehouse={van}
                        onChange={() => { }}
                        onUnsentChangesChange={() => { }}

                    />
                }

                <Flex w={"100%"} justify={"center"} gap={"md"} mt={"1rem"}>
                    {stockAdmin && <Link href={`/stock-take/create?wid=${van.ID}&empid=${van.EmployeeID}`}>
                        <Button>Stock Take</Button>
                    </Link>}
                    {stockTransfer &&
                        <>
                            <Button onClick={() => beginStockTransfer("in")}>Stock Transfer In</Button>
                            <Button onClick={() => beginStockTransfer("out")}>Stock Transfer Out</Button>
                        </>
                    }
                </Flex>

            </Card>

        </Container>

        <ManageStockTransactionDrawer
            stockTransaction={selectedStockTransaction}
            show={!!selectedStockTransaction || !!createStockTransactionType}
            purchaseOrderID={selectedStockTransaction?.PurchaseOrderID}
            stockTransactionType={selectedStockTransaction?.StockTransactionType || createStockTransactionType}
            onClose={((navigateToStockTransactionID: any) => {
                setCreateStockTransactionType(null);
                initialStockTransaction.current = null;
                if (navigateToStockTransactionID) {
                    stockTransactionService.getStockTransaction(navigateToStockTransactionID).then((st: any) => {
                        setSelectedStockTransaction(st);
                    });
                }
                else {
                    setSelectedStockTransaction(null);
                }
            }) as any}
            onSaved={() => {
                initialStockTransaction.current = null;
                setSelectedStockTransaction(null);
                setCreateStockTransactionType(null);
                setRefreshData((p: any) => !p)
            }}
            isNew={!!createStockTransactionType}
            heading={createStockTransactionType ? `Create ${Enums.getEnumStringValue(Enums.StockTransactionType, createStockTransactionType, true)}` :
                selectedStockTransaction?.StockTransactionType ? `${selectedStockTransaction?.StockTransactionStatus === Enums.StockTransactionStatus.Draft ? "Edit" : "View"} ${Enums.getEnumStringValue(Enums.StockTransactionType, selectedStockTransaction?.StockTransactionType, true)}` : undefined
            }
            initialValues={initialStockTransaction.current}
        />
    </>
    );
}

export default VanPage;

export const Route = createFileRoute('/_authenticated/van/$id')({ component: VanPage })
